import type { MetaDescriptor } from 'react-router';
import { SITE } from '~/data/site';
import { shippedServices } from '~/data/services';

/**
 * Page metadata + structured data.
 *
 * Every page calls `buildMeta` — title, description, canonical, Open Graph, and any
 * JSON-LD in one place, so no page can quietly ship without a canonical or a
 * description.
 */

interface PageMetaInput {
  /** 50–65 characters. The site name is appended automatically. */
  title: string;
  /** 150–160 characters. */
  description: string;
  /** Route path, e.g. "/pricing". */
  path: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Trim a description to fit a SERP snippet WITHOUT cutting mid-word.
 *
 * The bug this replaces: routes built their description with `.slice(0, 158)`, which
 * cuts at a character index. Five service pages shipped a description ending mid
 * sentence — "…Published on", "…is folded into" — which is the exact text a buyer
 * reads before deciding whether to click. Trimming at the last word boundary and
 * adding an ellipsis costs nothing and never produces a fragment.
 */
export function clampDescription(text: string, max = 158): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  // Leave room for the ellipsis, then walk back to the last space.
  const cut = clean.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(/[,;:.\u2014-]+$/, '')}\u2026`;
}

export function buildMeta(input: PageMetaInput): MetaDescriptor[] {
  const { title, description, path, noindex, jsonLd } = input;
  const canonical = `${SITE.url}${path === '/' ? '/' : path}`;
  const ogImage = `${SITE.url}/images/og-default.png`;

  const out: MetaDescriptor[] = [
    { title: `${title} | ${SITE.name}` },
    { name: 'description', content: clampDescription(description) },
    { name: 'robots', content: noindex ? 'noindex, nofollow' : 'index, follow' },
    { tagName: 'link', rel: 'canonical', href: canonical },

    { property: 'og:title', content: title },
    { property: 'og:description', content: clampDescription(description) },
    { property: 'og:url', content: canonical },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: SITE.name },
    { property: 'og:image', content: ogImage },

    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: ogImage },
  ];

  if (jsonLd) {
    for (const schema of Array.isArray(jsonLd) ? jsonLd : [jsonLd]) {
      out.push({ 'script:ld+json': schema } as MetaDescriptor);
    }
  }

  return out;
}

/**
 * The business itself — the node every other schema on the site points at.
 *
 * The `@id` (`{url}/#business`) is the important part: `serviceSchema` and the city
 * pages reference it with `{ '@id': ... }` instead of restating the company, so a
 * crawler reads one business with many services rather than twenty unrelated blobs.
 *
 * `areaServed` is derived from SITE.serviceCities, and the hours from
 * SITE.hours.officeSpec, so adding a city or changing a closing time is one edit and
 * the schema follows.
 */
export function localBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': `${SITE.url}/#business`,
    name: SITE.name,
    legalName: SITE.legalName,
    url: SITE.url,
    email: SITE.contact.email,
    description:
      'Commercial cleaning and nightly janitorial services for offices, medical practices, and multi-tenant buildings across Tampa Bay.',
    // Google will not render a LocalBusiness rich result without an image.
    image: `${SITE.url}/images/og-default.png`,
    logo: `${SITE.url}/brand/logo-wordmark-primary.svg`,
    // A $-band, not a dollar figure — this is the format the local pack expects.
    // The real published rates live on /pricing, which is the honest answer.
    priceRange: '$$',
    currenciesAccepted: 'USD',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Brandon',
      addressRegion: 'FL',
      postalCode: '33511',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: SITE.geo.lat,
      longitude: SITE.geo.lng,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [...SITE.hours.officeSpec.days],
        opens: SITE.hours.officeSpec.opens,
        closes: SITE.hours.officeSpec.closes,
      },
    ],
    areaServed: SITE.serviceCities.map((city) => ({
      '@type': 'City',
      name: `${city}, FL`,
    })),
    // Ties the five shipped services to this business as one catalog. Built from the
    // same data the /services pages render, so it can never advertise a cut service.
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Commercial cleaning services',
      itemListElement: shippedServices().map((service) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: service.name,
          description: service.blurb,
          url: `${SITE.url}/services/${service.slug}`,
        },
      })),
    },
    knowsAbout: ['Janitorial services', 'Commercial cleaning', 'Floor care', 'Post-construction cleaning'],
    naics: SITE.naics,
    // Omitted entirely when there is no verified profile to link. `sameAs: []` asserts
    // "this business exists nowhere else", which is a different and wrong claim.
    ...(SITE.profiles.length > 0 ? { sameAs: [...SITE.profiles] } : {}),
  };
}

/**
 * The site as a publication, and the company as an organisation.
 *
 * Emitted once, on the home page only. `WebSite` gives the domain a name a crawler can
 * use in a sitelinks header; `Organization` is the entity node that a Google Business
 * Profile eventually gets matched against. Both point back at `#business` so the three
 * nodes read as one company.
 */
export function siteIdentitySchema() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${SITE.url}/#website`,
        url: SITE.url,
        name: SITE.name,
        publisher: { '@id': `${SITE.url}/#business` },
        inLanguage: 'en-US',
      },
      {
        '@type': 'Organization',
        '@id': `${SITE.url}/#organization`,
        name: SITE.name,
        legalName: SITE.legalName,
        url: SITE.url,
        logo: `${SITE.url}/brand/logo-wordmark-primary.svg`,
        email: SITE.contact.email,
        foundingDate: '2025-07',
        ...(SITE.profiles.length > 0 ? { sameAs: [...SITE.profiles] } : {}),
      },
    ],
  };
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${SITE.url}${item.url}`,
    })),
  };
}

export function serviceSchema(input: { name: string; description: string; path: string }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: input.name,
    description: input.description,
    serviceType: input.name,
    url: `${SITE.url}${input.path}`,
    provider: { '@id': `${SITE.url}/#business` },
    areaServed: SITE.serviceCities.map((city) => ({ '@type': 'City', name: `${city}, FL` })),
  };
}

/**
 * A city page, as a Service scoped to one place.
 *
 * `provider` points at the `#business` node rather than restating the company, and
 * `areaServed` narrows to this one city — which is the whole point. A city page whose
 * areaServed listed all seven cities would be telling a crawler it is the same page as
 * the other six.
 */
export function citySchema(city: {
  slug: string;
  name: string;
  county: string;
  lede: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${SITE.url}/commercial-cleaning/${city.slug}#service`,
    name: `Commercial cleaning in ${city.name}, FL`,
    serviceType: 'Commercial cleaning',
    description: city.lede,
    url: `${SITE.url}/commercial-cleaning/${city.slug}`,
    provider: { '@id': `${SITE.url}/#business` },
    areaServed: {
      '@type': 'City',
      name: `${city.name}, FL`,
      containedInPlace: {
        '@type': 'AdministrativeArea',
        name: `${city.county} County, Florida`,
      },
    },
  };
}

export function faqSchema(qa: readonly { readonly q: string; readonly a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qa.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}
